import { useState } from 'react';
import { retentionApi } from '../../api/client';
import { LoadError, Loading } from '../../components/ui';
import { dateOnly, sizeOf } from '../../lib/format';
import { useData } from '../../lib/useData';

/**
 * Settings → Storage and retention → before Confirm (1.13.0, Teampace 3123, proposal 26; help:
 * storage): what the count would delete from Stitch's copy, month by month (each opens to its days),
 * in counts and sizes. The days add up to the count. It only reads.
 */

const n = (x: number) => x.toLocaleString('en-US');
const monthWords = (m: string) => new Date(`${m}-15T12:00:00Z`).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
const dayWords = (d: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });

export function RetentionPreview({ domain, runId }: { domain: string; runId: string }) {
  const data = useData(() => retentionApi.preview(domain, runId), [domain, runId], { live: false });
  const [open, setOpen] = useState<string | null>(null);
  const p = data.data;
  if (data.error) return <LoadError error={data.error} retry={data.reload} />;
  if (!p) return <Loading label="Working out what would go" />;
  if (!p.months.length) return <p className="muted small">Nothing in Stitch’s copy is from before {dateOnly(`${p.cutoff}T12:00:00Z`)} any more: nothing would go.</p>;
  const changed = p.now.items !== p.counted.items || p.now.bytes !== p.counted.bytes;
  return (
    <div className="stack retention-preview">
      <p className="small">
        What would go from Stitch’s copy: {n(p.now.items)} {p.noun} ({sizeOf(p.now.bytes)}) over {n(p.now.days)} {p.now.days === 1 ? 'day' : 'days'}, everything from before{' '}
        {dateOnly(`${p.cutoff}T12:00:00Z`)}.
        {p.holds ? ` What ${p.holds === 1 ? 'a legal hold covers' : `${p.holds} legal holds cover`} stays.` : ''}
        {changed ? ` (The count said ${n(p.counted.items)}, ${sizeOf(p.counted.bytes)}: the archive has changed since, and this is now.)` : ''}
      </p>
      <div className="table-wrap">
        <table className="table compact" aria-label={`${p.label}: what would go, by month`}>
          <thead>
            <tr>
              <th>Month</th>
              <th style={{ textAlign: 'right' }}>{p.noun.replace(/^./, (c) => c.toUpperCase())}</th>
              <th style={{ textAlign: 'right' }}>Size</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {p.months.map((m) => (
              <MonthRows key={m.month} m={m} open={open === m.month} toggle={() => setOpen(open === m.month ? null : m.month)} />
            ))}
          </tbody>
        </table>
      </div>
      <p className="muted small">Your own storage isn’t touched: what’s there is yours and stays.</p>
    </div>
  );
}

function MonthRows({ m, open, toggle }: { m: { month: string; items: number; bytes: number; days: { day: string; items: number; bytes: number }[] }; open: boolean; toggle: () => void }) {
  return (
    <>
      <tr>
        <td>{monthWords(m.month)}</td>
        <td style={{ textAlign: 'right' }}>{n(m.items)}</td>
        <td style={{ textAlign: 'right' }}>{sizeOf(m.bytes)}</td>
        <td>
          <button type="button" className="link-btn small" onClick={toggle} aria-expanded={open}>
            {open ? 'Hide days' : `${m.days.length} ${m.days.length === 1 ? 'day' : 'days'}`}
          </button>
        </td>
      </tr>
      {open &&
        m.days.map((d) => (
          <tr key={d.day} className="muted">
            <td style={{ paddingLeft: 24 }}>{dayWords(d.day)}</td>
            <td style={{ textAlign: 'right' }}>{n(d.items)}</td>
            <td style={{ textAlign: 'right' }}>{sizeOf(d.bytes)}</td>
            <td />
          </tr>
        ))}
    </>
  );
}
